import { useTranslation } from "react-i18next";

import type { CentinelaJornada, CentinelaTramo } from "../../../features/centinela/types";

/**
 * La regla de 24 horas: a qué hora estuvo haciendo qué.
 *
 * ── Por qué hace falta si ya están los totales ──────────────────────────────
 * «Tres horas y media al volante» y «manejó de 03:00 a 06:30» son dos hechos
 * distintos, y el segundo es el que se puede conversar con alguien. La regla
 * además deja ver de un vistazo cosas que un total esconde: que los cortes de
 * señal caen todos en el mismo tramo —cobertura, no la unidad—, o que el
 * descanso largo fue a mitad de la jornada y no al final.
 *
 * ── Esto NO es una bitácora de horas de servicio ────────────────────────────
 * Se parece a la regla de un ELD y ésa es la idea, pero hay una diferencia que
 * importa y por eso está escrita en pantalla: un ELD grafica el ESTADO QUE
 * DECLARA EL CONDUCTOR (off duty, litera, manejando, en servicio) y de ahí
 * salen los relojes legales. Acá no hay quién declare nada: lo que se grafica
 * es lo que hizo el VEHÍCULO, deducido de sus posiciones. Un camión detenido
 * con el conductor descargando y uno con el conductor dormido se ven igual, y
 * ningún reloj de esta pantalla sirve para acreditar cumplimiento.
 */

const COLOR: Record<CentinelaTramo["estado"], string> = {
  conduciendo: "bg-success",
  trafico: "bg-warning",
  ralenti: "bg-info",
  detenido: "bg-outline/50",
  sin_datos: "bg-error/30",
};

const ORDEN: CentinelaTramo["estado"][] = [
  "conduciendo",
  "trafico",
  "ralenti",
  "detenido",
  "sin_datos",
];

export interface ReglaDelDiaProps {
  /** La medición completa, no solo los tramos: hace falta para distinguir POR
   *  QUÉ está vacía, que es lo único útil cuando lo está. */
  medicion: CentinelaJornada | null;
  /** Inicio del día en epoch ms; la regla siempre cubre 24 h desde ahí. */
  inicioDia: number;
}

export default function ReglaDelDia({ medicion, inicioDia }: ReglaDelDiaProps) {
  const { t } = useTranslation("common");
  const DIA = 24 * 60 * 60 * 1000;
  const tramos: CentinelaTramo[] = medicion?.tramos ?? [];

  /*
    ── Tres vacíos distintos ────────────────────────────────────────────────
    El mensaje decía siempre «no hay posiciones suficientes», y la primera vez
    que la regla salió vacía en producción la causa era otra: el servidor era de
    antes de que existieran los tramos. Un mensaje que apunta al lugar
    equivocado cuesta más que no tener mensaje, porque manda a buscar donde no
    es. Se separan los tres casos.
  */
  if (!tramos.length) {
    const motivo = !medicion
      ? t("monitoreoIa.conduccion.reglaSinMedicion", {
        defaultValue: "No se pudo leer el historial de posiciones de este día.",
      })
      : (medicion.puntos ?? 0) > 1
        ? t("monitoreoIa.conduccion.reglaSinTramos", {
          defaultValue:
            "Este día sí tiene medición, pero el servidor todavía no envía el detalle por horas. Aparecerá con la próxima actualización de Centinela.",
        })
        : t("monitoreoIa.conduccion.reglaVacia", {
          defaultValue: "No hay posiciones suficientes para dibujar la regla de este día.",
        });

    return (
      <p className="rounded-xl bg-surface px-3 py-2 text-xs text-content-faint">{motivo}</p>
    );
  }

  // Se recorta a la ventana del día: un tramo puede empezar antes de la
  // medianoche y pintarlo entero desplazaría todo lo demás.
  const barras = tramos
    .map((x) => {
      const desde = Math.max(x.desde, inicioDia);
      const hasta = Math.min(x.hasta, inicioDia + DIA);
      return { ...x, desde, hasta };
    })
    .filter((x) => x.hasta > x.desde);

  const presentes = ORDEN.filter((e) => barras.some((b) => b.estado === e));

  return (
    <div className="rounded-xl border border-line bg-surface p-3">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <p className="text-xs font-semibold text-content">
          {t("monitoreoIa.conduccion.regla", { defaultValue: "La regla del día" })}
        </p>
        <p className="font-mono text-[9px] max-sm:text-[11px] uppercase tracking-wider text-content-muted">
          00 – 24 h
        </p>
      </div>

      <div className="relative h-7 overflow-hidden rounded-md bg-subtle">
        {barras.map((b, i) => (
          <span
            key={`${b.desde}-${i}`}
            className={`absolute top-0 h-full ${COLOR[b.estado]}`}
            style={{
              left: `${((b.desde - inicioDia) / DIA) * 100}%`,
              // Mínimo visible: un tramo de dos minutos es 0.14% y no se vería,
              // pero un hueco de dos minutos en medio de la jornada importa.
              width: `${Math.max(((b.hasta - b.desde) / DIA) * 100, 0.4)}%`,
            }}
            title={`${t(`monitoreoIa.conduccion.estado.${b.estado}`, { defaultValue: b.estado })} · ${new Date(b.desde).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}–${new Date(b.hasta).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
          />
        ))}
      </div>

      {/* Las horas, cada tres: con las 24 no se lee nada a este ancho. */}
      <div className="mt-1 flex justify-between font-mono text-[8px] max-sm:text-[10px] tabular-nums text-content-faint">
        {[0, 3, 6, 9, 12, 15, 18, 21, 24].map((h) => (
          <span key={h}>{String(h).padStart(2, "0")}</span>
        ))}
      </div>

      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
        {presentes.map((e) => (
          <span key={e} className="flex items-center gap-1.5 text-[10px] max-sm:text-[11px] text-content-secondary">
            <span className={`h-2 w-2 rounded-sm ${COLOR[e]}`} />
            {t(`monitoreoIa.conduccion.estado.${e}`, { defaultValue: e })}
          </span>
        ))}
      </div>

      {/* Sin esta línea, la regla se lee como una bitácora de horas de servicio
          y alguien va a querer usarla para acreditar cumplimiento. */}
      <p className="mt-2 text-[10px] max-sm:text-[11px] leading-relaxed text-content-faint">
        {t("monitoreoIa.conduccion.reglaNota", {
          defaultValue:
            "Es lo que hizo el vehículo, deducido de sus posiciones — no el estado que declara el conductor. Una unidad detenida con el operador descargando y una con el operador dormido se ven igual aquí.",
        })}
      </p>
    </div>
  );
}
